import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sa } from '../superAdminUi';

// A single-value donut for the "Today AI Usage" card: a grey track ring with a teal arc that
// sweeps clockwise from the top to represent the usage percentage, and the figure printed large
// in the centre with a caption beneath. Pure SVG — no charting dependency.

const Wrap = styled.div`
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;
`;
const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const Value = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.extrabold};
  font-size: ${(p) => Math.round(p.$size * 0.2)}px;
  line-height: 1;
  color: ${sa.color.text};
`;
const Caption = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.regular};
  font-size: 13px;
  color: ${sa.color.textGray};
  margin-top: 6px;
`;

export default function DonutChart({ pct, label, size, stroke, color, track }) {
  const value = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const arc = (value / 100) * circumference;
  const centre = size / 2;

  return (
    <Wrap $size={size} data-testid='usage-donut'>
      <svg width={size} height={size} role='img' aria-label={`${value}% ${label}`}>
        <circle cx={centre} cy={centre} r={radius} fill='none' stroke={track} strokeWidth={stroke} />
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill='none'
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={`${arc} ${circumference - arc}`}
          transform={`rotate(-90 ${centre} ${centre})`}
        />
      </svg>
      <Center>
        <Value $size={size}>{value}%</Value>
        <Caption>{label}</Caption>
      </Center>
    </Wrap>
  );
}

DonutChart.propTypes = {
  pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  size: PropTypes.number,
  stroke: PropTypes.number,
  color: PropTypes.string,
  track: PropTypes.string,
};

DonutChart.defaultProps = {
  label: 'Usage',
  size: 180,
  stroke: 18,
  color: sa.color.teal,
  track: sa.color.mist,
};
